import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box } from "@mui/material";
import { Selectbox } from "../../FormComponent";
import { getActiveAIModels } from "../../../utils/api/aimodel_apis";
import { STATUS } from "../../../utils/api/sharedfunction";

/** Internal select value for Auto — never exposed to parents. */
export const AIMODEL_AUTO_SENTINEL = "__AIMODEL_AUTO__";

const messages = {
  loading: "Loading AI models, please wait...",
  error: "Failed to fetch AI models. Please try again.",
};

/**
 * @param {Array<{ provider: string, model_key: string, is_default?: boolean, is_recommended?: boolean }>} models
 */
export function resolveDefaultConcreteModel(models) {
  if (!models?.length) return null;
  const byDefault = models.find((m) => m.is_default && m.provider && m.model_key);
  if (byDefault) return { ...byDefault };
  const byRec = models.find((m) => m.is_recommended && m.provider && m.model_key);
  if (byRec) return { ...byRec };
  const first = models[0];
  if (first?.provider && first.model_key) return { ...first };
  return null;
}

/**
 * Map internal select value to the concrete model object passed to onModelChange.
 * Auto sentinel always resolves to org default (never forwarded as-is).
 */
export function resolveRuntimeModelForParent(internalValue, models) {
  if (!models?.length) return null;
  if (
    !internalValue ||
    internalValue === AIMODEL_AUTO_SENTINEL ||
    internalValue === ""
  ) {
    return resolveDefaultConcreteModel(models);
  }
  try {
    const { provider, model_key } = JSON.parse(internalValue);
    const full = models.find(
      (m) => m.provider === provider && m.model_key === model_key
    );
    if (full) return { ...full };
    return resolveDefaultConcreteModel(models);
  } catch {
    return resolveDefaultConcreteModel(models);
  }
}

function buildOptionLabel(m) {
  return m.display_name || m.model_key;
}

function buildOptionChips(m) {
  const chips = [];
  if (m.is_default) chips.push("default");
  if (m.is_recommended) chips.push("recommended");
  return chips;
}

/**
 * Runtime AI model selector: fetches by feature, always offers Auto (concrete default to parent).
 * Does not call updateFeatureModel or write Redux.
 */
export function AIModelRuntimeSelector({
  featureKey,
  trialOrgSkip = false,
  canSelectAiModel = true,
  projectId = null,
  onModelChange,
  /** When set (e.g. edit schedule), select this model after catalog load instead of Auto. */
  initialModel = null,
  disabled = false,
  readOnly = false,
  disableClearable = true,
  label = "Select LLM Model",
  helperText: helperTextProp,
  error = null,
  openUpward = false,
  size = "medium",
  required = false,
  className,
}) {
  const [models, setModels] = useState([]);
  const [internalValue, setInternalValue] = useState(AIMODEL_AUTO_SENTINEL);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const onModelChangeRef = useRef(onModelChange);
  useEffect(() => {
    onModelChangeRef.current = onModelChange;
  }, [onModelChange]);

  const notifyParent = useCallback((value, list) => {
    const resolved = resolveRuntimeModelForParent(value, list);
    const isAuto = !value || value === AIMODEL_AUTO_SENTINEL || value === "";
    onModelChangeRef.current?.(resolved, { isAuto });
  }, []);

  const load = useCallback(async () => {
    if (!featureKey) {
      setModels([]);
      setLoading(false);
      return;
    }
    // Read-only "Auto" user (no GET_AI_MODEL, non-trial): never fetch the catalog.
    // Emit a server-resolve marker carrying the featureKey; the backend resolves the
    // org default at run time. This both fixes the catalog leak and avoids needing a
    // concrete model the user isn't allowed to see.
    // Trial is excluded: trialOrgSkip has its own synthetic-model path below (the studio
    // passes canSelectAiModel=false for trial too, so this guard must let trial fall through).
    if (!canSelectAiModel && !trialOrgSkip) {
      setModels([]);
      setInternalValue(AIMODEL_AUTO_SENTINEL);
      setFetchError(false);
      setLoading(false);
      onModelChangeRef.current?.(null, { isAuto: true, featureKey, serverResolve: true });
      return;
    }
    setLoading(true);
    setFetchError(false);
    try {
      const response = await getActiveAIModels(featureKey, { trialOrgSkip, projectId });
      if (response.status === STATUS.SUCCESS) {
        const data = response.data || [];
        setModels(data);
        const initialProvider = initialModel?.provider;
        const initialKey = initialModel?.model_key || initialModel?.modelKey;
        const hasInitial = Boolean(initialProvider && initialKey);
        const initialMatch = hasInitial
          ? data.find((m) => m.provider === initialProvider && m.model_key === initialKey)
          : null;
        if (initialMatch) {
          const val = JSON.stringify({
            provider: initialMatch.provider,
            model_key: initialMatch.model_key,
          });
          setInternalValue(val);
          notifyParent(val, data);
        } else {
          setInternalValue(AIMODEL_AUTO_SENTINEL);
          notifyParent(AIMODEL_AUTO_SENTINEL, data);
        }
      } else {
        throw new Error(response.error || "Failed to fetch AI models");
      }
    } catch (e) {
      console.error("AIModelRuntimeSelector:", e);
      setFetchError(true);
      setModels([]);
      onModelChangeRef.current?.(null);
    } finally {
      setLoading(false);
    }
  }, [featureKey, trialOrgSkip, canSelectAiModel, projectId, initialModel, notifyParent]);

  useEffect(() => {
    load();
  }, [load]);

  const options = [
    ...(models.length > 0 || !canSelectAiModel
      ? [{ value: AIMODEL_AUTO_SENTINEL, label: "Auto" }]
      : []),
    ...models.map((m) => ({
      value: JSON.stringify({ provider: m.provider, model_key: m.model_key }),
      label: buildOptionLabel(m),
      chips: buildOptionChips(m),
    })),
  ];

  const handleChange = (event) => {
    if (readOnly) return;
    const val = event.target.value;
    setInternalValue(val);
    notifyParent(val, models);
  };

  const helperText =
    helperTextProp !== undefined
      ? helperTextProp
      : loading
        ? messages.loading
        : fetchError
          ? messages.error
          : "";

  return (
    <Box
      className={className}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: 2,
        width: "100%",
      }}
    >
      <Selectbox
        label={label}
        value={internalValue}
        onChange={handleChange}
        options={options}
        disabled={disabled || loading}
        readOnly={readOnly}
        disableClearable={disableClearable}
        openUpward={openUpward}
        size={size}
        required={required}
        helperText={helperText}
        error={error}
      />
    </Box>
  );
}

export default AIModelRuntimeSelector;
